/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useParams } from 'react-router';
import Loading from "@/shared/ui/components/basic/loading/Loading";
import { PropertiesService } from "@services/requests";
import { useProperty } from "@features/property-management/pages/PropertyContext";
import { PropertyDetailView } from "@features/property-management/components/PropertyDetailView";
import type { RouteParams } from "./App.agencyRoutes";

/**
 * LoggedPropertyRoutes component.
 *
 * @returns {JSX.Element} React component element
 */
export const LoggedPropertyRoutes = () => {
  const { agency_id, property_id } = useParams<RouteParams>();

  const { property, fetchId, setProperty, setEditMode, loaded, error, setLoadState } =
    useProperty((state) => state);

  const editMode =
    new URLSearchParams(window.location.search).get("mode") === "edit";

  useEffect(() => {
    if (agency_id && property_id) {
      PropertiesService.getProperty(agency_id, property_id)
        .then((propertyResponse) => {
          setProperty(propertyResponse);
          setLoadState(true, null);
          setEditMode(editMode);
          window.history.pushState({}, "", window.location.pathname);
        })
        .catch((error) => {
          // Surface the failure inline on the detail page (handled by
          // PropertyDetailView's terminal error branch) instead of raising the
          // global popup + toast, which would double-notify for a full-page load.
          setLoadState(true, error);
        });
    }
  }, [property_id, fetchId]);

  // Delegate rendering to PropertyDetailView once the fetch has resolved into a
  // terminal error so it can show the inline retry/back error state.
  // Loading state is now handled inside PropertyDetailView via PropertyDetailSkeleton.

  return (
    <Suspense fallback={<Loading mode="section" />}>
      <Routes>
        <Route path="" element={<PropertyDetailView />} />
        <Route path="*" element={<Navigate to="" />} />
      </Routes>
    </Suspense>
  );
};
